import { tokenSignal, logout } from '../state/auth.store';
import { isGeminiModalOpenSignal } from '../state/ui.store';

export interface ApiFetchOptions extends RequestInit {
  timeout?: number;
}

/**
 * Determina o timeout em milissegundos para a requisição:
 * - 60s para operações demoradas (ex: /summarize ou /sync-history)
 * - 25s por padrão para evitar travamento em conexões móveis instáveis
 * - Permite sobrescrever explicitamente através de options.timeout
 */
export function getApiTimeout(endpoint: string, customTimeout?: number): number {
  if (typeof customTimeout === 'number' && customTimeout > 0) {
    return customTimeout;
  }
  const isLongRunning = endpoint.includes('/summarize') || endpoint.includes('/sync-history');
  return isLongRunning ? 60000 : 25000;
}

export async function apiFetch<T = any>(endpoint: string, options: ApiFetchOptions = {}): Promise<T> {
  const token = tokenSignal.value;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> || {})
  };

  const timeoutMs = getApiTimeout(endpoint, options.timeout);
  let controller: AbortController | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let signal = options.signal;

  // Se o chamador já forneceu um signal de cancelamento, respeita-o.
  // Caso contrário, cria um AbortController com proteção de timeout.
  if (!signal) {
    controller = new AbortController();
    signal = controller.signal;
    timeoutId = setTimeout(() => {
      controller?.abort(new Error(`Timeout de requisição (${timeoutMs / 1000}s) excedido para ${endpoint}`));
    }, timeoutMs);
  }

  let response: Response;
  try {
    response = await fetch(`/api${endpoint}`, { ...options, headers, signal });
  } catch (fetchErr: any) {
    if (controller?.signal.aborted) {
      const timeoutErr = new Error(`Tempo limite de requisição excedido (${timeoutMs / 1000}s) ao chamar ${endpoint}`);
      (timeoutErr as any).name = 'TimeoutError';
      (timeoutErr as any).cause = fetchErr;
      throw timeoutErr;
    }
    throw fetchErr;
  } finally {
    // Garante a liberação do timer quando o fetch resolve ou rejeita
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  if (response.status === 401 || response.status === 403) {
    if (endpoint !== '/auth/login') {
      logout();
    }
    const text = await response.text().catch(() => '');
    let data: any = {};
    try { data = JSON.parse(text); } catch {}
    const errObj = new Error(data.error || 'Senha ou token inválido.');
    (errObj as any).status = response.status;
    throw errObj;
  }

  const text = await response.text().catch(() => '');
  let data: any = {};
  try {
    data = JSON.parse(text);
  } catch {
    data = { error: response.ok ? text : `Erro no servidor (${response.status})` };
  }

  if (!response.ok) {
    const errorMsg = data.details ? `${data.error}: ${data.details}` : (data.error || 'Erro na requisição.');
    const errObj = new Error(errorMsg);
    (errObj as any).details = data.details;
    (errObj as any).code = data.code;

    if (data.code === 'GEMINI_KEY_MISSING' || (data.error && typeof data.error === 'string' && data.error.includes('GEMINI_API_KEY'))) {
      isGeminiModalOpenSignal.value = true;
    }

    throw errObj;
  }

  return data as T;
}

